import { Cancel, CheckCircle, Person, Send, Sync } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  sendApplicantEmailVerificationCode,
  verifyApplicantEmailCode,
} from "apis/controllers/auth";
import { validateIban } from "apis/controllers/externalData";
import { useLookup } from "context/lookupContext";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { ColumnLabel } from "qubCommon/Inputs";
import FormCheckbox from "qubCommon/Inputs/Checkbox";
import ClientAutocomplete from "qubCommon/Inputs/ClientAutocomplete";
import Dropdown from "qubCommon/Inputs/Dropdown";
import IbanInput from "qubCommon/Inputs/IbanInput";
import ReviewSection from "qubCommon/ReviewSection";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import TextInput from "src/qubCommon/Inputs/TextInput";

const hasValue = (value) => value !== undefined && value !== null && value !== "";
const MALE_GENDER_ID = 1;
const EMPTY_OPTIONS = [];
const MOBILE_PHONE_ERROR_MESSAGE =
  "Το κινητό τηλέφωνο πρέπει να ξεκινά από 69 και να αποτελείται από 10 ψηφία.";

const normalizeMobilePhone = (value) => String(value ?? "").replace(/\s+/g, "");
const isValidMobilePhone = (value) => /^69\d{8}$/.test(normalizeMobilePhone(value));
const getLookupId = (option) => option?.id ?? option?.Id ?? null;
const getLookupName = (option) => option?.name ?? option?.Name ?? "";

const getApplicantVerifiedEmail = (applicant) => {
  if (!applicant) return "";

  if (applicant?.emailVerification?.verifiedEmail) {
    return applicant.emailVerification.verifiedEmail;
  }

  if (applicant?.verifiedEmail) return applicant.verifiedEmail;

  if (
    applicant?.emailVerification?.isVerified ||
    applicant?.emailVerification?.verified ||
    applicant?.emailConfirmed ||
    applicant?.isEmailVerified ||
    applicant?.emailVerified
  ) {
    return applicant?.email || "";
  }

  return "";
};

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email || "");

const isSuccessResponse = (res) =>
  res?.status === "success" || res?.succeeded === true || res?.succeded === true;

const getResponseMessage = (res, fallback) => {
  if (typeof res?.data === "string") return res.data;

  return (
    res?.message ||
    res?.data?.message ||
    res?.response?.data?.message ||
    res?.error ||
    fallback
  );
};

const isAlreadyVerifiedResponse = (res) =>
  /ήδη|already/i.test(getResponseMessage(res, "") || "");

export const EmailVerificationPanel = ({
  applicant,
  canEdit,
  canPartiallyEdit,
  isLoadedEmailVerified,
  shouldShowVerification,
  forceVisible,
  onVerified,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const email = useWatch({ control, name: "applicant.email" }) || "";
  const verifiedEmail =
    useWatch({
      control,
      name: "applicantEmailVerification.verifiedEmail",
    }) || "";
  const emailIsVerified =
    useWatch({
      control,
      name: "applicantEmailVerification.isVerified",
    }) === true;
  const codeSentEmail =
    useWatch({
      control,
      name: "applicantEmailVerification.codeSentEmail",
    }) || "";
  const canChangeEmail = canEdit || canPartiallyEdit;
  const isVerified =
    hasValue(email) &&
    emailIsVerified &&
    verifiedEmail === email;
  const emailChangedAfterVerification =
    hasValue(email) && hasValue(verifiedEmail) && verifiedEmail !== email;
  const canSendCode = canChangeEmail && isValidEmail(email) && !isVerified;
  const shouldShowCodeInput =
    canSendCode && codeSentEmail === email;
  const [loadingAction, setLoadingAction] = useState(null);
  const [message, setMessage] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const autoSentEmailRef = useRef("");
  const shouldRenderPanel =
    hasValue(email) &&
    (forceVisible ||
      shouldShowVerification ||
      canSendCode ||
      isVerified ||
      emailChangedAfterVerification ||
      shouldShowCodeInput ||
      !!message);

  useEffect(() => {
    const setVerificationValue = (name, value) => {
      if (getValues(name) !== value) {
        setValue(name, value, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    };

    const verifiedEmail =
      getApplicantVerifiedEmail(applicant) ||
      (isLoadedEmailVerified ? applicant?.email || "" : "");

    setVerificationValue(
      "applicantEmailVerification.verifiedEmail",
      verifiedEmail
    );
    setVerificationValue(
      "applicantEmailVerification.isVerified",
      hasValue(verifiedEmail) && verifiedEmail === applicant?.email
    );
    setVerificationValue("applicantEmailVerification.code", "");
    setVerificationValue("applicantEmailVerification.codeSentEmail", "");
  }, [
    applicant?.email,
    applicant?.emailVerification?.isVerified,
    applicant?.emailVerification?.verified,
    applicant?.emailVerification?.verifiedEmail,
    applicant?.emailVerified,
    applicant?.isEmailVerified,
    applicant?.verifiedEmail,
    getValues,
    isLoadedEmailVerified,
    setValue,
  ]);

  const handleSendCode = useCallback(async () => {
    if (!canSendCode || loadingAction || cooldownSeconds > 0) return;

    setLoadingAction("send");
    setMessage(null);

    try {
      const res = await sendApplicantEmailVerificationCode({ email });

      if (isSuccessResponse(res)) {
        if (isAlreadyVerifiedResponse(res)) {
          setValue("applicantEmailVerification.verifiedEmail", email, {
            shouldDirty: false,
            shouldValidate: false,
          });
          setValue("applicantEmailVerification.isVerified", true, {
            shouldDirty: false,
            shouldValidate: false,
          });
          setValue("applicantEmailVerification.code", "", {
            shouldDirty: false,
            shouldValidate: false,
          });
          setValue("applicantEmailVerification.codeSentEmail", "", {
            shouldDirty: false,
            shouldValidate: false,
          });
          onVerified?.();
          setMessage({
            severity: "success",
            text: getResponseMessage(
              res,
              "Το email είναι ήδη επιβεβαιωμένο."
            ),
          });
          return;
        }

        setValue("applicantEmailVerification.code", "", {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue("applicantEmailVerification.codeSentEmail", email, {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue("applicantEmailVerification.isVerified", false, {
          shouldDirty: false,
          shouldValidate: false,
        });
        setMessage({
          severity: "info",
          text: getResponseMessage(
            res,
            "Ο κωδικός επιβεβαίωσης στάλθηκε στο email."
          ),
        });
        setCooldownSeconds(60);
      } else {
        setMessage({
          severity: "error",
          text: getResponseMessage(
            res,
            "Δεν ήταν δυνατή η αποστολή κωδικού."
          ),
        });
      }
    } catch (err) {
      setMessage({
        severity: "error",
        text: getResponseMessage(
          err,
          "Δεν ήταν δυνατή η αποστολή κωδικού."
        ),
      });
    } finally {
      setLoadingAction(null);
    }
  }, [
    canSendCode,
    cooldownSeconds,
    email,
    loadingAction,
    onVerified,
    setValue,
  ]);

  useEffect(() => {
    if (
      !shouldShowVerification ||
      !canSendCode ||
      codeSentEmail === email ||
      autoSentEmailRef.current === email
    ) {
      return;
    }

    autoSentEmailRef.current = email;
    handleSendCode();
  }, [
    canSendCode,
    codeSentEmail,
    email,
    handleSendCode,
    shouldShowVerification,
  ]);

  const handleVerifyCode = async () => {
    if (!shouldShowCodeInput || loadingAction) return;

    const code = getValues("applicantEmailVerification.code");

    setLoadingAction("verify");
    setMessage(null);

    try {
      const res = await verifyApplicantEmailCode({
        code,
      });

      if (isSuccessResponse(res)) {
        setValue("applicantEmailVerification.verifiedEmail", email, {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue("applicantEmailVerification.isVerified", true, {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue("applicantEmailVerification.code", "", {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue("applicantEmailVerification.codeSentEmail", "", {
          shouldDirty: false,
          shouldValidate: false,
        });
        onVerified?.();
        setMessage({
          severity: "success",
          text: getResponseMessage(
            res,
            "Το email επιβεβαιώθηκε με επιτυχία."
          ),
        });
      } else {
        setMessage({
          severity: "error",
          text: getResponseMessage(
            res,
            "Ο κωδικός επιβεβαίωσης δεν είναι σωστός."
          ),
        });
      }
    } catch (err) {
      setMessage({
        severity: "error",
        text: getResponseMessage(
          err,
          "Δεν ήταν δυνατή η επιβεβαίωση του email."
        ),
      });
    } finally {
      setLoadingAction(null);
    }
  };

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;

    const timer = window.setTimeout(() => {
      setCooldownSeconds((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [cooldownSeconds]);

  if (!shouldRenderPanel) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 0.75,
        border: "1px solid",
        borderColor: isVerified ? "#A8DDB5" : "#F3C766",
        borderRadius: "8px",
        bgcolor: isVerified ? "#F4FBF5" : "#FFF9EA",
        px: 1,
        py: 0.75,
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            {isVerified ? (
              <CheckCircle color="success" sx={{ fontSize: 18 }} />
            ) : (
              <Cancel sx={{ color: "#A35B00", fontSize: 18 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: isVerified ? "success.dark" : "#8A4B00",
                fontWeight: 700,
              }}
            >
              {isVerified
                ? "Email επιβεβαιωμένο"
                : "Email μη επιβεβαιωμένο"}
            </Typography>
          </Stack>
          {canSendCode && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleSendCode}
              sx={{ color: "#fefefe" }}
              disabled={loadingAction !== null || cooldownSeconds > 0}
              startIcon={
                loadingAction === "send" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : <Send color="inherit" fontSize="small" />
              }
            >
              {cooldownSeconds > 0
                ? `Επαναποστολή σε ${cooldownSeconds}s`
                : shouldShowCodeInput
                  ? "Επαναποστολή"
                  : "Επιβεβαίωση Email"}
            </Button>
          )}
        </Stack>

        {!isVerified && hasValue(email) && !isValidEmail(email) && (
          <Typography variant="caption" color="error">
            Συμπληρώστε έγκυρη διεύθυνση email για επιβεβαίωση.
          </Typography>
        )}

        {shouldShowCodeInput && (
          <Grid container spacing={1} alignItems="flex-end">
            <Grid item xs={12} sm={7}>
              <TextInput
                name="applicantEmailVerification.code"
                label="Κωδικός επιβεβαίωσης"
                disabled={!canChangeEmail}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyCode}
                disabled={loadingAction !== null}
                startIcon={
                  loadingAction === "verify" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                Επιβεβαίωση
              </Button>
            </Grid>
          </Grid>
        )}

        {!canChangeEmail && hasValue(email) && !isVerified && (
          <Typography variant="caption" color="text.secondary">
            Το email δεν έχει επιβεβαιωθεί.
          </Typography>
        )}

        {message && (
          <Typography
            variant="caption"
            color={message.severity === "error" ? "error" : "text.secondary"}
          >
            {message.text}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const MothersInfo = ({
  applicant,
  review,
  canEdit = false,
  canApprove,
  canPartiallyEdit,
  canViewReview,
  canViewWSResult,
  canEditIban,
  useLegacyPhoneFields,
  isLoadedEmailVerified,
  shouldShowEmailVerification,
  forceEmailVerification,
  onEmailVerified,
}) => {
  const { lookups } = useLookup();
  const form = useFormContext();
  const [checkInfo, setCheckInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobilePhoneError, setMobilePhoneError] = useState("");
  const { id } = useParams();
  const singleParentOptions = lookups?.singleParentOptions || EMPTY_OPTIONS;
  const isMaleApplicant = Number(applicant?.gender?.id) === MALE_GENDER_ID;
  const loadedSingleParentOption =
    applicant?.singleParentOption || applicant?.SingleParentOption;
  const isMotherSingleParent =
    useWatch({
      control: form.control,
      name: "applicant.isMotherSingleParent",
    }) === true;
  const hasSingleParentFamily = isMotherSingleParent || isMaleApplicant;
  const selectedSingleParentOptionId = useWatch({
    control: form.control,
    name: "applicant.singleParentOption.id",
  });
  const applicantSingleParentOptionId = getLookupId(loadedSingleParentOption);
  const effectiveSingleParentOptionId =
    selectedSingleParentOptionId === undefined
      ? applicantSingleParentOptionId
      : selectedSingleParentOptionId;
  const applicantSingleParentOptionName = getLookupName(
    loadedSingleParentOption
  );

  const clearHusbandValues = useCallback(() => {
    form.setValue("husbandsHelp", []);
    form.setValue("husbands", null);
    form.setValue("husbands.created", []);
    form.setValue("husbands.updated", []);
    form.setValue("husbands.deleted", []);
    form.setValue("applicant.husbandsHelp", []);
    form.setValue("applicant.husbands.created", []);
    form.setValue("applicant.husbands.updated", []);
    form.setValue("applicant.husbands.deleted", []);
  }, [form]);

  const syncSingleParentOption = useCallback(
    (option, setValueOptions = {}) => {
      form.setValue(
        "applicant.singleParentOption",
        option
          ? {
            id: getLookupId(option),
            name: getLookupName(option),
          }
          : null,
        setValueOptions
      );
    },
    [form]
  );

  const handleMobilePhoneBlur = useCallback(
    (e) => {
      const normalizedValue = normalizeMobilePhone(e.target.value);

      if (normalizedValue !== e.target.value) {
        e.target.value = normalizedValue;
        form.setValue("applicant.mobilePhone", normalizedValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }

      setMobilePhoneError(
        isValidMobilePhone(normalizedValue) ? "" : MOBILE_PHONE_ERROR_MESSAGE
      );
    },
    [form]
  );

  useEffect(() => {
    if (useLegacyPhoneFields) setMobilePhoneError("");
  }, [useLegacyPhoneFields]);

  const handleCheck = async () => {
    setLoading(true);
    try {
      const result = await validateIban(id);
      setCheckInfo(result?.data);
      if (result?.data?.status === "success")
        form.setValue("reviews.applicant.status", "2");
      if (result?.data?.status === "error")
        form.setValue("reviews.applicant.status", "3");
    } catch (err) {
      console.error("Error during check:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (review?.wsResponse?.message) {
      setCheckInfo({
        status: review?.wsResponse?.status,
        bank: review?.wsResponse?.bank,
        timestamp: review?.wsResponse?.timestamp,
        message: review?.wsResponse?.message,
      });
    }
  }, [review]);

  useEffect(() => {
    if (applicant?.address?.municipality === null)
      form.setValue("applicant.address.municipality", null);
  }, [applicant]);

  useEffect(() => {
    if (!isMaleApplicant) return;

    if (form.getValues("applicant.isMotherSingleParent") !== true) {
      form.setValue("applicant.isMotherSingleParent", true, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

  }, [applicant?.id, form, isMaleApplicant]);

  useEffect(() => {
    const setValueOptions = {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    };

    if (!hasSingleParentFamily) {
      syncSingleParentOption(null, setValueOptions);
      return;
    }

    if (!effectiveSingleParentOptionId) {
      syncSingleParentOption(null, setValueOptions);
      return;
    }

    const selectedOption = singleParentOptions.find(
      (option) =>
        String(getLookupId(option)) === String(effectiveSingleParentOptionId)
    );

    syncSingleParentOption(
      selectedOption || {
        id: effectiveSingleParentOptionId,
        name: applicantSingleParentOptionName,
      },
      setValueOptions
    );
  }, [
    applicantSingleParentOptionName,
    effectiveSingleParentOptionId,
    form,
    hasSingleParentFamily,
    singleParentOptions,
    syncSingleParentOption,
  ]);

  const AccordionTitle = () => (
    <Stack direction="row" gap={1}>
      <Person color="primary" fontSize="small" sx={{ marginLeft: "2px" }} />

      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΣΤΟΙΧΕΙΑ ΔΙΚΑΙΟΥΧΟΥ
      </Typography>
      <Typography color="primary" sx={{ marginLeft: "18px", fontWeight: 500 }}>
        {applicant?.lastName} {applicant?.firstName}
      </Typography>
    </Stack>
  );

  useEffect(() => {
    if (!applicant?.address?.municipality)
      form.setValue("applicant.address.municipality", null);
  }, [applicant?.address?.municipality]);

  const renderResult = () => {
    if (!checkInfo) return null;

    const { status, timestamp, bank } = checkInfo;

    return (
      <Box mt={2}>
        {status === "success" ? (
          <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
            {checkInfo?.message}
          </Alert>
        ) : (
          <Alert icon={<Cancel fontSize="inherit" />} severity="error">
            {checkInfo?.message}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" mt={1}>
          Τελευταίος Έλεγχος: {timestamp}
        </Typography>
      </Box>
    );
  };

  const AccordionContent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.ama" label="Α.Μ.ΟΓΑ/ΕΦΚΑ*" />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Stack direction="column" gap={1}>
          <ColumnLabel label="Οικογένεια" />
          <FormCheckbox
            name="applicant.isMotherSingleParent"
            label="Μονογονεϊκή"
            disabled={!canEdit || isMaleApplicant}
            changeFn={(e) => {
              if (e.target.checked) {
              }
            }}
          />

        </Stack>
      </Grid>
      {isMotherSingleParent && <Grid item xs={12} sm={4}>
        <Dropdown
          name="applicant.singleParentOption.id"
          label="Επιλογή μονογονεϊκής οικογένειας"
          valueName="id"
          labelName="name"
          options={singleParentOptions}
          defaultValue={applicantSingleParentOptionId}
          disabled={!canEdit || !hasSingleParentFamily}
          changeFn={(e) => {
            const selectedOption = singleParentOptions.find(
              (option) =>
                String(getLookupId(option)) === String(e.target.value)
            );

            syncSingleParentOption(selectedOption || null);
          }}
        />
      </Grid>}

      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.amka" label="AMKA*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.afm" label="ΑΦΜ*" />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.firstName" label="Όνομα*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.lastName" label="Επώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.fatherName" label="Πατρώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.motherName" label="Μητρώνυμο*" />
      </Grid>
      {/* <Grid item xs={12} sm={6} sm={3}>
        <TextInput
          disabled
          name="applicant.fatherName"
          label="Πατρώνυμο"
          defaultValue={applicant?.fatherName}
        />
      </Grid> */}
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.phone1"
          label="Τηλέφωνο 1*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name={useLegacyPhoneFields ? "applicant.phone2" : "applicant.mobilePhone"}
          label={useLegacyPhoneFields ? "Τηλέφωνο 2" : "Κινητό τηλέφωνο* (69XXXXXXXX)"}
          blurFn={useLegacyPhoneFields ? undefined : handleMobilePhoneBlur}
          error={!useLegacyPhoneFields && !!mobilePhoneError}
          helperText={!useLegacyPhoneFields ? mobilePhoneError : undefined}
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack spacing={0.75}>
          <TextInput
            name="applicant.email"
            label="Email*"
            disabled={!canEdit && !canPartiallyEdit}
          />
          <EmailVerificationPanel
            applicant={applicant}
            canEdit={canEdit}
            canPartiallyEdit={canPartiallyEdit}
            isLoadedEmailVerified={isLoadedEmailVerified}
            shouldShowVerification={shouldShowEmailVerification}
            forceVisible={forceEmailVerification}
            onVerified={onEmailVerified}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <IbanInput
          name="applicant.iban"
          label="IBAN*"
          disabled={!canEdit && !canEditIban}
        />
      </Grid>
      <Grid item xs={12} sm={8} md={8}>
        <TextInput
          name="applicant.address.addressLine"
          label="Διεύθυνση*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>

      <Grid item xs={12} sm={4} md={4} lg={4}>
        <TextInput
          name="applicant.address.postalCode"
          label="ΤΚ*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.address.city"
          label="Πόλη*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ClientAutocomplete
          name="applicant.address.municipality.id"
          label="Δήμος*"
          valueName="id"
          labelName="name"
          customChangeFn={(e) => {
            if (!e) form.setValue("applicant.address.municipality", null);
          }}
          options={lookups?.municipalities || []}
          defaultValue={applicant?.address?.municipality?.id}
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>

      <Grid item xs={12}>
        {canApprove && (
          <Button
            onClick={handleCheck}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <Sync />
              )
            }
            disabled={loading}
          >
            WebService: IBAN/ΑΦΜ
          </Button>
        )}

        {(canViewWSResult || canApprove) && renderResult()}
      </Grid>
      <Grid item xs={12}>
        <ReviewSection
          canApprove={canApprove}
          canViewReview={canViewReview}
          fieldPrefix="reviews.applicant"
        />
      </Grid>
    </Grid>
  );

  return (
    <CustomSimpleAccordion
      title={<AccordionTitle />}
      content={AccordionContent()}
    />
  );
};

export default MothersInfo;
